import { useState, useCallback } from 'react';

/**
 * A `useState`-like hook backed by `sessionStorage`.
 *
 * On mount the stored value is read (JSON-parsed); if absent or unreadable,
 * `initialValue` is used. Every update is written back to `sessionStorage`.
 * SSR-safe: falls back to `initialValue` when `window` is unavailable.
 *
 * @template T
 * @param {string} key - The sessionStorage key.
 * @param {T} [initialValue=false] - Default value when nothing is stored.
 * @returns {[T, (newValue: T) => void]} Current value and setter.
 */
export function useSessionStorageState(key, initialValue = false) {
	const [value, setValue] = useState(() => {
		try {
			if (typeof window === 'undefined') {
				return initialValue;
			}
			const stored = sessionStorage.getItem(key);
			return stored !== null ? JSON.parse(stored) : initialValue;
		} catch {
			return initialValue;
		}
	});

	const set = useCallback(
		newValue => {
			try {
				sessionStorage.setItem(key, JSON.stringify(newValue));
			} catch {
				// sessionStorage may be unavailable
			}
			setValue(newValue);
		},
		[key]
	);

	return [value, set];
}
